import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import type { PingPayout } from "../services/api";
import { PingPayoutStatus } from "./PingPayoutStatus";
import {
  AmountBreakdownTable,
  type AmountBreakdownRow,
} from "./AmountBreakdownTable";
import {
  deriveProjectFundsTotals,
  type ProjectHeldFundsBreakdownItem,
  type ProjectHeldFundsSummary as HeldFundsSummary,
} from "../utils/projectHeldFunds";

type ProjectHeldFundsSummaryProps = {
  summary: HeldFundsSummary;
  pingPayouts?: PingPayout[];
  /** The viewer's payment timeline, for the paid out/refunded/deposited totals. */
  transactions?: any[];
};

const statusTranslationKey = (status: string) => {
  if (status === "partially_released") return "partiallyReleased";
  if (status === "partially_refunded") return "partiallyRefunded";
  return "held";
};

export function ProjectHeldFundsSummary({
  summary,
  pingPayouts = [],
  transactions = [],
}: ProjectHeldFundsSummaryProps) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  if (summary.items.length === 0) return null;

  const locale = i18n.resolvedLanguage || i18n.language || "sv-SE";
  const formatAmount = (amountOre: number, currency: string) =>
    new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amountOre / 100);

  const itemReference = (item: ProjectHeldFundsBreakdownItem) => {
    const parts = [
      item.contractNumber
        ? t("projectView.transactions.heldFunds.contract", {
            number: item.contractNumber,
            defaultValue: `Kontrakt #${item.contractNumber}`,
          })
        : null,
      item.milestoneNumber
        ? t("projectView.transactions.heldFunds.milestone", {
            number: item.milestoneNumber,
            defaultValue: `Etappmål #${item.milestoneNumber}`,
          })
        : null,
    ].filter(Boolean);
    return parts.length
      ? parts.join(" · ")
      : t(
          "projectView.transactions.heldFunds.unknownReference",
          "Kontrakt och etapp saknas",
        );
  };

  const totals = deriveProjectFundsTotals(transactions);
  const totalRows = [
    {
      key: "held",
      label: t("projectView.transactions.heldFunds.summaryBox.held", "Hålls"),
      amountOre: summary.amountOre,
    },
    {
      key: "paidOut",
      label: t("projectView.transactions.heldFunds.summaryBox.paidOut", "Utbetalt"),
      amountOre: totals.paidOutOre,
    },
    {
      key: "refunded",
      label: t("projectView.transactions.heldFunds.summaryBox.refunded", "Återbetalt"),
      amountOre: totals.refundedOre,
    },
    ...(totals.depositedOre === null
      ? []
      : [
          {
            key: "deposited",
            label: t(
              "projectView.transactions.heldFunds.summaryBox.deposited",
              "Totalt insatt",
            ),
            amountOre: totals.depositedOre,
          },
        ]),
  ];

  const stageRows: AmountBreakdownRow[] = summary.items.map((item) => {
    const payouts = pingPayouts.filter(
      (payout) =>
        payout.contractId === item.contractId &&
        payout.milestoneId === item.milestoneId,
    );
    return {
      key: `${item.contractId ?? item.contractNumber}:${item.milestoneId ?? item.milestoneNumber}`,
      label: (
        <div className="min-w-0 text-break">
          <div className="fw-semibold">{itemReference(item)}</div>
          {item.milestoneDescription && (
            <div className="text-explain">{item.milestoneDescription}</div>
          )}
          {payouts.length > 0 ? (
            payouts.map((payout) => (
              <PingPayoutStatus
                key={payout.id}
                payout={payout}
                canUseDebugBank={false}
              />
            ))
          ) : (
            <span className="badge held-funds-status-pill mt-1">
              {t(
                `projectView.transactions.heldFunds.status.${statusTranslationKey(item.status)}`,
                item.status === "partially_released"
                  ? "Delvis utbetald"
                  : item.status === "partially_refunded"
                    ? "Delvis återbetald"
                    : "Hålls",
              )}
            </span>
          )}
        </div>
      ),
      amount: formatAmount(item.amountOre, item.currency),
      amountClassName: "fw-bold text-nowrap",
    };
  });

  return (
    <section
      className="card project-status mb-0 held-funds-card"
      aria-labelledby="project-held-funds-title"
    >
      <header className="project-status-header">
        <h2 id="project-held-funds-title" className="project-status-title">
          <i className="feather-lock" aria-hidden="true"></i>
          {t(
            "projectView.transactions.heldFunds.title",
            "Medel som hålls i projektet",
          )}
        </h2>
      </header>
      <div
        className="project-section-body"
        data-testid="project-held-funds-summary"
      >
        <button
          type="button"
          className="btn w-100 text-start held-funds-toggle"
          aria-expanded={expanded}
          aria-controls="project-held-funds-breakdown"
          onClick={() => setExpanded((current) => !current)}
        >
          {/* The amount and its explanation on the left, the toggle on the right. */}
          <span className="held-funds-toggle-row">
            <span className="held-funds-toggle-text">
              <span className="held-funds-toggle-amount">
                {formatAmount(summary.amountOre, summary.currency)}
              </span>
              <span className="text-explain">
                {t(
                  "projectView.transactions.heldFunds.summary",
                  "Summerat kvarhållet belopp för alla kontrakt och etapper.",
                )}
              </span>
            </span>
            <span className="held-funds-toggle-action">
              <span className="d-flex align-items-center gap-1 text-nowrap">
                {expanded
                  ? t(
                      "projectView.transactions.heldFunds.collapse",
                      "Dölj fördelning",
                    )
                  : t(
                      "projectView.transactions.heldFunds.expand",
                      "Visa fördelning",
                    )}
                <i
                  className={`feather-chevron-${expanded ? "up" : "down"}`}
                  aria-hidden="true"
                />
              </span>
            </span>
          </span>
        </button>
        {expanded && (
          <div id="project-held-funds-breakdown" className="held-funds-breakdown">
            <div className="row g-3">
              <div className="col-12 col-md-7 col-xl-12">
                <h3 className="held-funds-subtitle">
                  {t(
                    "projectView.transactions.heldFunds.breakdownTitle",
                    "Fördelning per kontrakt och etapp",
                  )}
                </h3>
                <AmountBreakdownTable rows={stageRows} />
              </div>
              {/* Summary first on mobile and at xl (the card is half width there), to the right at md–lg. */}
              <div className="col-12 col-md-5 col-xl-12 order-first order-md-0 order-xl-first">
                <div className="held-funds-summary-box">
                  <h3 className="held-funds-subtitle">
                    {t(
                      "projectView.transactions.heldFunds.summaryBox.title",
                      "Sammanställning",
                    )}
                  </h3>
                  <dl className="held-funds-totals mb-0">
                    {totalRows.map((row) => (
                      <div className="held-funds-total" key={row.key}>
                        <dt>{row.label}</dt>
                        <dd className="text-nowrap">
                          {formatAmount(row.amountOre, summary.currency)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
